import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Copy,
  Coins,
  MoreHorizontal,
  Pause,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { apiErrorMessage } from "@/api/perkpoint/client";
import {
  FraudApi,
  fraudKeys,
  type ApiFraudAlert,
  type ApiFraudRule,
  type FraudAlertQuery,
  type FraudRuleInput,
  type FraudSettings,
} from "@/api/perkpoint/fraud";
import { useActiveProgramId } from "./programs/program-scope";
import {
  ALERT_TYPE_LABELS,
  SEVERITY_DOT,
  SEVERITY_LABELS,
  SEVERITY_STYLES,
  STATUS_LABELS,
  STATUS_STYLES,
  formatDateTime,
  formatMinor,
  riskTone,
  type AlertStatus,
  type AlertType,
  type Severity,
} from "./fraud/fraud-data";
import { FraudRuleDialog } from "./fraud/FraudRuleDialog";
import { AlertDetailSheet } from "./fraud/AlertDetailSheet";
import { FraudEmptyState } from "./fraud/FraudEmptyState";

type StatusFilter = "all" | AlertStatus;
const STATUS_FILTERS: StatusFilter[] = ["all", "active", "investigating", "resolved", "false_positive"];
const SEVERITY_ORDER: Severity[] = ["critical", "high", "medium", "low"];
const PAGE_SIZE = 20;

const isOpenAlert = (alert: ApiFraudAlert) => alert.status === "active" || alert.status === "investigating";

function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
        SEVERITY_STYLES[severity],
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", SEVERITY_DOT[severity])} />
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

function StatusBadge({ status }: { status: AlertStatus }) {
  return (
    <span className={cn("inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_STYLES[status])}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function BarList({ rows }: { rows: { label: string; value: number; tone: string }[] }) {
  const max = Math.max(1, ...rows.map((row) => row.value));
  return (
    <ul className="space-y-2.5">
      {rows.map((row) => (
        <li key={row.label} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-700">{row.label}</span>
            <span className="tabular-nums text-slate-900">{row.value}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className={cn("h-full rounded-full", row.tone)} style={{ width: `${(row.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Real-time fraud detection for the active program: the alert queue, the rules, and settings. */
const FraudDetection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { programId, isLoading: programLoading } = useActiveProgramId();
  const [tab, setTab] = useState("alerts");

  // Alert filters. Search waits for typing to pause before it queries.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [severityFilter, setSeverityFilter] = useState<"all" | Severity>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | AlertType>("all");
  const [page, setPage] = useState(1);
  const [selectedAlert, setSelectedAlert] = useState<ApiFraudAlert | null>(null);

  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ApiFraudRule | undefined>();
  const [deletingRule, setDeletingRule] = useState<ApiFraudRule | null>(null);
  const [threshold, setThreshold] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setPage(1), [debouncedSearch, statusFilter, severityFilter, typeFilter]);

  const alertQuery: FraudAlertQuery = {
    status: statusFilter === "all" ? undefined : statusFilter,
    severity: severityFilter === "all" ? undefined : severityFilter,
    type: typeFilter === "all" ? undefined : typeFilter,
    search: debouncedSearch || undefined,
    page,
    page_size: PAGE_SIZE,
  };

  const summary = useQuery({
    queryKey: fraudKeys.summary(programId ?? ""),
    queryFn: () => FraudApi.summary(programId!),
    enabled: !!programId,
  });
  const alerts = useQuery({
    queryKey: fraudKeys.alerts(programId ?? "", alertQuery),
    queryFn: () => FraudApi.alerts(programId!, alertQuery),
    enabled: !!programId,
    placeholderData: keepPreviousData,
  });
  const rules = useQuery({
    queryKey: fraudKeys.rules(programId ?? ""),
    queryFn: () => FraudApi.rules(programId!),
    enabled: !!programId,
  });

  const currency = summary.data?.currency ?? "";
  const exponent = summary.data?.currency_exponent ?? 2;
  const settings = summary.data?.settings;
  useEffect(() => {
    if (settings) setThreshold(settings.auto_block_threshold);
  }, [settings]);

  // Every change can move the counts, the queue and the rule triggers, so refresh the lot.
  const refresh = () => queryClient.invalidateQueries({ queryKey: fraudKeys.program(programId ?? "") });
  const fail = (title: string) => (error: unknown) =>
    toast({ title, description: apiErrorMessage(error), variant: "destructive" });

  const alertStatus = useMutation({
    mutationFn: ({ alert, status }: { alert: ApiFraudAlert; status: AlertStatus }) =>
      FraudApi.updateAlert(programId!, alert.id, status),
    onSuccess: (updated) => {
      if (selectedAlert?.id === updated.id) setSelectedAlert(updated);
      toast({ title: `Alert ${updated.code} updated`, description: `Marked as ${STATUS_LABELS[updated.status].toLowerCase()}.` });
      refresh();
    },
    onError: fail("Could not update the alert"),
  });

  const blockMember = useMutation({
    mutationFn: (alert: ApiFraudAlert) => FraudApi.blockMember(programId!, alert.id),
    onSuccess: ({ alert, resolved_alerts }) => {
      if (selectedAlert?.id === alert.id) setSelectedAlert(alert);
      toast({
        title: `${alert.member.name} blocked`,
        description: `Future earn and burn is stopped. ${resolved_alerts.length} open alert${
          resolved_alerts.length === 1 ? "" : "s"
        } resolved.`,
      });
      refresh();
    },
    onError: fail("Could not block the member"),
  });

  const saveRule = useMutation({
    mutationFn: ({ rule, input }: { rule?: ApiFraudRule; input: Partial<FraudRuleInput> }) =>
      rule ? FraudApi.updateRule(programId!, rule.id, input) : FraudApi.createRule(programId!, input as FraudRuleInput),
    onSuccess: () => refresh(),
  });

  const deleteRule = useMutation({
    mutationFn: (rule: ApiFraudRule) => FraudApi.deleteRule(programId!, rule.id),
    onSuccess: (_, rule) => {
      toast({ title: "Rule deleted", description: rule.name });
      setDeletingRule(null);
      refresh();
    },
    onError: fail("Could not delete the rule"),
  });

  const saveSettings = useMutation({
    mutationFn: (input: Partial<Omit<FraudSettings, "updated_at">>) => FraudApi.saveSettings(programId!, input),
    onSuccess: () => refresh(),
    onError: (error) => {
      fail("Could not save settings")(error);
      refresh();
    },
  });

  const openNewRule = () => {
    setEditingRule(undefined);
    setRuleDialogOpen(true);
  };

  // The dialog awaits this: it closes on success and stays open (with the error toast) on failure.
  const submitRule = async (input: FraudRuleInput) => {
    try {
      const saved = await saveRule.mutateAsync({ rule: editingRule, input });
      toast({
        title: editingRule ? "Rule updated" : "Rule created",
        description: saved.enabled ? `${saved.code} is checking every transaction.` : `${saved.code} saved as off.`,
      });
      if (!editingRule) setTab("rules");
    } catch (error) {
      fail(editingRule ? "Could not save the rule" : "Could not create the rule")(error);
      throw error;
    }
  };

  const toggleRule = (rule: ApiFraudRule, enabled: boolean) =>
    saveRule.mutate(
      { rule, input: { status: enabled ? "active" : "paused" } },
      {
        onSuccess: () => toast({ title: enabled ? "Rule turned on" : "Rule turned off", description: rule.name }),
        onError: fail("Could not change the rule"),
      },
    );

  // A copy starts off so it cannot change live behaviour until someone turns it on.
  const duplicateRule = (rule: ApiFraudRule) =>
    saveRule.mutate(
      {
        input: {
          name: `${rule.name} (copy)`,
          outlets: rule.outlets,
          severity: rule.severity,
          status: "paused",
          velocity: rule.velocity,
          invoice: rule.invoice ? { amount: rule.invoice.amount, action: rule.invoice.action } : null,
          redemption: rule.redemption,
        },
      },
      {
        onSuccess: () => toast({ title: "Rule duplicated", description: "The copy is off until you turn it on." }),
        onError: fail("Could not duplicate the rule"),
      },
    );

  const filtersActive = search !== "" || statusFilter !== "all" || severityFilter !== "all" || typeFilter !== "all";
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSeverityFilter("all");
    setTypeFilter("all");
  };

  const s = summary.data;
  const kpis = [
    { label: "Open alerts", value: s?.alerts.active, meta: "Waiting for review", icon: AlertTriangle, tone: "text-red-600" },
    { label: "High & critical", value: s?.alerts.high_risk_open, meta: "Among unresolved alerts", icon: ShieldAlert, tone: "text-orange-600" },
    {
      label: "Blocked (30 days)",
      value: s?.alerts.blocked_last_30_days,
      meta: "Transactions refused",
      icon: Ban,
      tone: "text-slate-700",
    },
    {
      label: "Value at risk",
      value: s ? formatMinor(s.at_risk.amount_minor, currency, exponent) : undefined,
      meta: s ? `${s.at_risk.points.toLocaleString("en-US")} points on unresolved alerts` : "On unresolved alerts",
      icon: Coins,
      tone: "text-slate-700",
    },
    {
      label: "Active rules",
      value: s ? `${s.rules.active}/${s.rules.total}` : undefined,
      meta: s?.alerts.precision_pct != null ? `${s.alerts.precision_pct}% of closed alerts confirmed` : "Checking every transaction",
      icon: ShieldCheck,
      tone: "text-green-700",
    },
  ];

  const severityRows = useMemo(
    () => SEVERITY_ORDER.map((severity) => ({
      label: SEVERITY_LABELS[severity],
      value: s?.open_by_severity[severity] ?? 0,
      tone: SEVERITY_DOT[severity],
    })),
    [s],
  );
  const typeRows = useMemo(
    () => (["velocity", "threshold"] as AlertType[]).map((type) => ({
      label: ALERT_TYPE_LABELS[type],
      value: s?.open_by_type[type] ?? 0,
      tone: "bg-slate-700",
    })),
    [s],
  );

  const hasUnresolved = !!s && s.alerts.active + s.alerts.investigating > 0;

  if (!programLoading && !programId) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <ShieldCheck className="mx-auto h-8 w-8 text-slate-400" />
        <h1 className="mt-3 text-xl font-semibold text-slate-900">No program selected</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fraud detection works on one program. Open a program as manager to review its alerts and rules.
        </p>
      </div>
    );
  }

  const monitoring = settings?.monitoring_enabled ?? true;
  const alertPage = alerts.data;
  const firstRow = alertPage && alertPage.total ? (alertPage.page - 1) * alertPage.page_size + 1 : 0;
  const lastRow = alertPage ? Math.min(alertPage.total, alertPage.page * alertPage.page_size) : 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Fraud detection</h1>
            {settings && (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                  monitoring ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600",
                )}
              >
                {monitoring ? (
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                  </span>
                ) : (
                  <Pause className="h-3 w-3" />
                )}
                {monitoring ? "Monitoring live" : "Monitoring paused"}
              </span>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-muted-foreground">
            Every earn and redemption is checked against your active rules. Review what they catch here.
          </p>
        </div>
        <Button onClick={openNewRule} disabled={!programId} className="bg-green-600 text-white hover:bg-green-700">
          <Plus className="mr-2 h-4 w-4" /> New fraud rule
        </Button>
      </div>

      {summary.isError && (
        <Card className="border-red-200 bg-red-50 shadow-none">
          <CardContent className="flex items-center justify-between gap-3 p-4 text-sm text-red-700">
            {apiErrorMessage(summary.error, "Could not load fraud detection")}
            <Button variant="outline" size="sm" className="bg-white" onClick={() => refresh()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{kpi.label}</p>
                <kpi.icon className={cn("h-4 w-4", kpi.tone)} />
              </div>
              {kpi.value === undefined ? (
                <Skeleton className="mt-3 h-8 w-20" />
              ) : (
                <p className="mt-2 text-2xl font-bold tabular-nums text-slate-900 sm:text-3xl">{kpi.value}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">{kpi.meta}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="alerts">
            Alerts
            {!!s?.alerts.active && (
              <span className="ml-2 rounded-full bg-red-100 px-1.5 text-xs font-semibold text-red-700">{s.alerts.active}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="rules">
            Rules {rules.data && <span className="ml-2 text-xs text-muted-foreground">{rules.data.total}</span>}
          </TabsTrigger>
        </TabsList>

        {/* Alerts */}
        <TabsContent value="alerts" className="mt-0">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
            <Card className="min-w-0 border-slate-200 shadow-sm">
              <CardContent className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div className="flex flex-wrap gap-1.5">
                    {STATUS_FILTERS.map((status) => {
                      const count =
                        status === "active" ? s?.alerts.active : status === "investigating" ? s?.alerts.investigating : undefined;
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setStatusFilter(status)}
                          className={cn(
                            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                            statusFilter === status
                              ? "border-slate-900 bg-slate-900 text-white"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                          )}
                        >
                          {status === "all" ? "All" : STATUS_LABELS[status]}
                          {count !== undefined && <span className="ml-1.5 tabular-nums opacity-70">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative sm:w-56">
                      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Alert, member ID or reference"
                        className="h-9 pl-8"
                      />
                    </div>
                    <Select value={severityFilter} onValueChange={(value) => setSeverityFilter(value as "all" | Severity)}>
                      <SelectTrigger className="h-9 sm:w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any severity</SelectItem>
                        {SEVERITY_ORDER.map((severity) => (
                          <SelectItem key={severity} value={severity}>
                            {SEVERITY_LABELS[severity]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as "all" | AlertType)}>
                      <SelectTrigger className="h-9 sm:w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any signal</SelectItem>
                        <SelectItem value="velocity">{ALERT_TYPE_LABELS.velocity}</SelectItem>
                        <SelectItem value="threshold">{ALERT_TYPE_LABELS.threshold}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                        <TableHead>Alert</TableHead>
                        <TableHead>Member</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Risk</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-12 text-right">
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alerts.isLoading || programLoading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell colSpan={6}>
                              <Skeleton className="h-9 w-full" />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : alerts.isError ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={6}>
                            <FraudEmptyState
                              variant="error"
                              title="Could not load alerts"
                              description={apiErrorMessage(alerts.error, "The fraud service did not respond.")}
                              action={
                                <Button variant="outline" size="sm" onClick={() => alerts.refetch()}>
                                  Try again
                                </Button>
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ) : !alertPage?.items.length ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={6}>
                            {filtersActive ? (
                              <FraudEmptyState
                                variant="filtered"
                                title="No alerts match these filters"
                                description="Try another status, severity or search term."
                                action={
                                  <Button variant="outline" size="sm" onClick={clearFilters}>
                                    Clear filters
                                  </Button>
                                }
                              />
                            ) : (
                              <FraudEmptyState
                                variant="alerts"
                                title="All clear"
                                description={
                                  rules.data?.items.length
                                    ? "No alerts yet. They appear here when a transaction trips one of your rules."
                                    : "No alerts yet. Create a fraud rule so transactions are checked."
                                }
                                action={
                                  !rules.data?.items.length && (
                                    <Button size="sm" onClick={openNewRule} className="bg-green-600 text-white hover:bg-green-700">
                                      <Plus className="mr-2 h-4 w-4" /> New fraud rule
                                    </Button>
                                  )
                                }
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        alertPage.items.map((alert) => (
                          <TableRow key={alert.id} className="cursor-pointer" onClick={() => setSelectedAlert(alert)}>
                            <TableCell className="max-w-[300px]">
                              <p className="truncate font-medium text-slate-900">{alert.description}</p>
                              <p className="text-xs text-muted-foreground">
                                {alert.code} · {alert.rule?.code ?? "Auto-block"} · {formatDateTime(alert.last_hit_at)}
                                {alert.hit_count > 1 && ` · ×${alert.hit_count}`}
                              </p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-slate-900">{alert.member.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {alert.member.external_member_id}
                                {alert.member.status === "blocked" && <span className="ml-1 text-red-600">· Blocked</span>}
                              </p>
                            </TableCell>
                            <TableCell>
                              <SeverityBadge severity={alert.severity} />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-14 overflow-hidden rounded-full bg-slate-100">
                                  <div
                                    className={cn("h-full rounded-full", riskTone(alert.risk_score))}
                                    style={{ width: `${alert.risk_score}%` }}
                                  />
                                </div>
                                <span className="text-sm tabular-nums text-slate-700">{alert.risk_score}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={alert.status} />
                            </TableCell>
                            <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${alert.code}`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => setSelectedAlert(alert)}>View details</DropdownMenuItem>
                                  {isOpenAlert(alert) ? (
                                    <>
                                      {alert.status === "active" && (
                                        <DropdownMenuItem onClick={() => alertStatus.mutate({ alert, status: "investigating" })}>
                                          <Search className="mr-2 h-4 w-4" /> Start investigating
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => alertStatus.mutate({ alert, status: "resolved" })}>
                                        <CheckCircle2 className="mr-2 h-4 w-4" /> Resolve
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => alertStatus.mutate({ alert, status: "false_positive" })}>
                                        <XCircle className="mr-2 h-4 w-4" /> False positive
                                      </DropdownMenuItem>
                                      {alert.member.status !== "blocked" && (
                                        <>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            className="text-red-600 focus:text-red-600"
                                            onClick={() => blockMember.mutate(alert)}
                                          >
                                            <Ban className="mr-2 h-4 w-4" /> Block member
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <DropdownMenuItem onClick={() => alertStatus.mutate({ alert, status: "active" })}>
                                      <RefreshCw className="mr-2 h-4 w-4" /> Reopen
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {alertPage && alertPage.total > PAGE_SIZE && (
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      Showing {firstRow}–{lastRow} of {alertPage.total}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={lastRow >= alertPage.total}
                        onClick={() => setPage((value) => value + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="space-y-4 p-5">
                  <h2 className="text-base font-semibold text-slate-900">Unresolved by severity</h2>
                  {s && !hasUnresolved ? (
                    <FraudEmptyState variant="chart" compact title="Nothing unresolved" description="Open alerts are grouped here." />
                  ) : (
                    <BarList rows={severityRows} />
                  )}
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="space-y-4 p-5">
                  <h2 className="text-base font-semibold text-slate-900">Unresolved by signal</h2>
                  {s && !hasUnresolved ? (
                    <FraudEmptyState variant="chart" compact title="Nothing unresolved" description="Velocity and threshold hits show here." />
                  ) : (
                    <BarList rows={typeRows} />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Rules */}
        <TabsContent value="rules" className="mt-0">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="min-w-0 space-y-3">
              {rules.isLoading || programLoading ? (
                Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-28 w-full rounded-lg" />)
              ) : rules.isError ? (
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <FraudEmptyState
                      variant="error"
                      title="Could not load fraud rules"
                      description={apiErrorMessage(rules.error, "The fraud service did not respond.")}
                      action={
                        <Button variant="outline" size="sm" onClick={() => rules.refetch()}>
                          Try again
                        </Button>
                      }
                    />
                  </CardContent>
                </Card>
              ) : !rules.data?.items.length ? (
                <Card className="border-dashed border-slate-300 shadow-none">
                  <CardContent className="p-4">
                    <FraudEmptyState
                      variant="rules"
                      title="No fraud rules yet"
                      description="Rules check every earn and redemption, then flag the transaction, refuse it, or block the member."
                      action={
                        <Button onClick={openNewRule} className="bg-green-600 text-white hover:bg-green-700">
                          <Plus className="mr-2 h-4 w-4" /> Create first rule
                        </Button>
                      }
                    />
                  </CardContent>
                </Card>
              ) : (
                rules.data.items.map((rule) => (
                  <Card key={rule.id} className={cn("border-slate-200 shadow-sm", !rule.enabled && "bg-slate-50/60")}>
                    <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:p-5">
                      <div className="min-w-0 flex-1 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className={cn("font-semibold", rule.enabled ? "text-slate-900" : "text-slate-500")}>
                            {rule.name}
                          </h3>
                          <span className="text-xs text-muted-foreground">{rule.code}</span>
                          <SeverityBadge severity={rule.severity} />
                          {!rule.enabled && (
                            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                              Off
                            </span>
                          )}
                        </div>
                        <ul className="space-y-1.5">
                          {rule.summary.map((line) => (
                            <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
                              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                              {line}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-muted-foreground">
                          {rule.outlet_label} · {rule.triggers} trigger{rule.triggers === 1 ? "" : "s"}
                          {rule.last_triggered_at && ` · last ${formatDateTime(rule.last_triggered_at)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.enabled}
                          disabled={saveRule.isPending}
                          onCheckedChange={(enabled) => toggleRule(rule, enabled)}
                          aria-label={`Turn ${rule.name} ${rule.enabled ? "off" : "on"}`}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${rule.name}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingRule(rule);
                                setRuleDialogOpen(true);
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => duplicateRule(rule)}>
                              <Copy className="mr-2 h-4 w-4" /> Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => setDeletingRule(rule)}>
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            <Card className="h-fit border-slate-200 shadow-sm">
              <CardContent className="space-y-5 p-5">
                <h2 className="text-base font-semibold text-slate-900">Automation</h2>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Real-time monitoring</p>
                    <p className="text-xs text-muted-foreground">
                      Check every earn and redemption. Off lets everything through without alerts.
                    </p>
                  </div>
                  <Switch
                    checked={monitoring}
                    disabled={!settings || saveSettings.isPending}
                    onCheckedChange={(value) => saveSettings.mutate({ monitoring_enabled: value })}
                    aria-label="Real-time monitoring"
                  />
                </div>
                <div className="space-y-3 border-t border-slate-100 pt-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Auto-block high risk</p>
                      <p className="text-xs text-muted-foreground">
                        Block the member when a hit's risk reaches the threshold, even on flag-only rules.
                      </p>
                    </div>
                    <Switch
                      checked={settings?.auto_block_enabled ?? false}
                      disabled={!settings || saveSettings.isPending}
                      onCheckedChange={(value) => saveSettings.mutate({ auto_block_enabled: value })}
                      aria-label="Auto-block high risk"
                    />
                  </div>
                  <div className={cn("space-y-2", !settings?.auto_block_enabled && "opacity-50")}>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Risk score at or above</span>
                      <span className="font-medium tabular-nums text-slate-900">{threshold ?? "–"}</span>
                    </div>
                    {/* Saves when the drag ends, not on every step. */}
                    <Slider
                      value={[threshold ?? 90]}
                      onValueChange={([value]) => setThreshold(value)}
                      onValueCommit={([value]) => saveSettings.mutate({ auto_block_threshold: value })}
                      min={50}
                      max={100}
                      step={1}
                      disabled={!settings?.auto_block_enabled || saveSettings.isPending}
                      aria-label="Auto-block threshold"
                    />
                    <p className="text-xs text-muted-foreground">
                      Scores start at 35 (low), 55 (medium), 75 (high) or 90 (critical), plus up to 10 for how far a
                      transaction is over the limit.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <FraudRuleDialog
        open={ruleDialogOpen}
        onOpenChange={setRuleDialogOpen}
        rule={editingRule}
        currency={currency || "AED"}
        programId={programId}
        onSave={submitRule}
      />

      <AlertDetailSheet
        alert={selectedAlert}
        currency={currency}
        currencyExponent={exponent}
        busy={alertStatus.isPending || blockMember.isPending}
        onOpenChange={(open) => !open && setSelectedAlert(null)}
        onStatusChange={(alert, status) => alertStatus.mutate({ alert, status })}
        onBlockMember={(alert) => blockMember.mutate(alert)}
      />

      <AlertDialog open={!!deletingRule} onOpenChange={(open) => !open && setDeletingRule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deletingRule?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Transactions stop being checked against this rule. Alerts it already raised stay in the queue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteRule.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (deletingRule) deleteRule.mutate(deletingRule);
              }}
            >
              Delete rule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FraudDetection;
